<script lang="ts">
  export * from './TextEditor.types';
</script>

<script setup lang="ts">
  // the side effects of the init file are cached by the browser and only run once per page load
  import './TextEditor.init';
  import { sanitizeHtml } from '@leaflink/stash-utils';
  import { uniqueId } from 'es-toolkit/compat';
  import Quill from 'quill';
  import { computed, onBeforeUnmount, onMounted, ref, useCssModule, watch } from 'vue';

  import Button from '../Button/Button.vue';
  import type { FieldProps } from '../Field/Field.types';
  import Field from '../Field/Field.vue';
  import type { IconName } from '../Icon/Icon.vue';
  import Icon from '../Icon/Icon.vue';
  import {
    TEXT_EDITOR_DEFAULT_CONTROL_HANDLERS,
    TEXT_EDITOR_DEFAULT_TOOLBAR_CONTROLS,
    TEXT_EDITOR_ICON_MAP,
  } from './TextEditor.constants';
  import type {
    TextEditorAllowedControlName,
    TextEditorControl,
    TextEditorControlHandlerMap,
    TextEditorControlMatrix,
    TextEditorControls,
    TextEditorFormat,
    TextEditorFormatGroups,
  } from './TextEditor.types';
  import { toCssLength } from './TextEditor.utils';

  export interface TextEditorProps extends FieldProps {
    /**
     * The model value.
     */
    modelValue?: string;

    /**
     * Placeholder text to display when the editor is empty.
     */
    placeholder?: string;

    /**
     * A set of custom toolbar controls to pass to the Quill editor.
     */
    controls?: TextEditorControls;

    /**
     * An object map of custom handlers to pass to the Quill editor.
     */
    handlers?: TextEditorControlHandlerMap;

    /**
     * Minimum height of the editor content area. A plain number is treated as pixels.
     */
    minHeight?: string | number;

    /**
     * Maximum height of the editor content area; taller content scrolls. A plain number is treated as pixels.
     */
    maxHeight?: string | number;
  }

  const props = withDefaults(defineProps<TextEditorProps>(), {
    modelValue: '',
    placeholder: '',
    controls: () => [],
    handlers: () => ({}),
    minHeight: 150,
    maxHeight: 400,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the model value changes.
     */
    'update:model-value': [value: string];

    /**
     * Emitted when an editor text is in focus (including an empty space).
     */
    focus: [editor: Quill];

    /**
     * Emitted when an editor text loses focus (including an empty space).
     */
    blur: [editor: Quill];
  }>();

  const classes = useCssModule();

  const editorRef = ref<HTMLElement | null>(null);
  let editorInstance: Quill | null = null;
  const editorId = computed(() => props.id || uniqueId('stash-text-editor-'));
  const model = ref(props.modelValue);

  const computedControls = computed(() => {
    const controls = props.controls.length ? props.controls : TEXT_EDITOR_DEFAULT_TOOLBAR_CONTROLS;
    return Array.isArray(controls[0])
      ? getFormatGroups(controls as TextEditorControlMatrix)
      : getFormat(controls as TextEditorControl);
  });

  const computedHandlers = computed(() => ({ ...TEXT_EDITOR_DEFAULT_CONTROL_HANDLERS, ...props.handlers }));

  const minHeightCss = computed(() => toCssLength(props.minHeight));
  const maxHeightCss = computed(() => toCssLength(props.maxHeight));

  // Function to check if the content is truly empty (handles Quill's <p><br></p> issue)
  // @see https://github.com/vueup/vue-quill/issues/484#issuecomment-2022876220
  function isTextContentEmpty(value: string): boolean {
    return value.replace(/<(.|\n)*?>/g, '').trim().length === 0;
  }

  function getControlIcon(control: TextEditorAllowedControlName, value?: string) {
    const icon = TEXT_EDITOR_ICON_MAP[control];

    if (!icon && !value) return;

    if (value && icon instanceof Object) {
      return icon[value] as IconName;
    }

    return icon as IconName;
  }

  function getFormat(controls: TextEditorControl): TextEditorFormat[] {
    return controls.map((control) => {
      const format = control instanceof Object ? (Object.keys(control)[0] as TextEditorAllowedControlName) : control;
      const value = control instanceof Object ? (control[format] as string) : undefined;

      return {
        icon: getControlIcon(format, value),
        name: format,
        value,
      };
    });
  }

  function getFormatGroups(controls: TextEditorControlMatrix): TextEditorFormatGroups {
    return controls.map((control) => getFormat(control));
  }

  // Convert modelValue HTML to Delta and replace editor content.
  // Sanitize first so stored or pasted HTML can't inject scripts or event handlers into the editor.
  function pasteHTML(quill: Quill) {
    const cleanedHtml = sanitizeHtml(props.modelValue);
    model.value = cleanedHtml;
    const delta = quill.clipboard.convert({ html: cleanedHtml });
    quill.setContents(delta);
  }

  onMounted(() => {
    const quill = new Quill(editorRef.value as HTMLElement, {
      modules: {
        toolbar: {
          container: `#${editorId.value}-format-bar`,
          handlers: computedHandlers.value,
        },
      },
      placeholder: props.placeholder,
      readOnly: props.isReadOnly,
      // Use our custom theme (registered in TextEditor.init.ts) — NOT 'snow'.
      // See that file's comment for why.
      theme: 'stash-snow',
    });

    if (!props.isReadOnly && props.isDisabled) {
      quill.enable(false);
    }

    // Set editor initial model
    if (props.modelValue) {
      pasteHTML(quill);
    }

    // Handle editor selection change, emit blur and focus
    quill.on('selection-change', (range) => {
      if (!range) {
        emit('blur', quill);
      } else {
        emit('focus', quill);
      }
    });

    // Handle editor text change
    quill.on('text-change', () => {
      model.value = quill.root.innerHTML;
    });

    editorInstance = quill;
  });

  // Watch modelValue and paste HTML if has changes
  watch(
    () => props.modelValue,
    (newValue) => {
      if (!editorInstance) return;
      if (newValue && newValue !== model.value && !isTextContentEmpty(newValue)) {
        pasteHTML(editorInstance);
        // ensuring the model gets the sanitized pasted html content
        model.value = editorInstance.root.innerHTML;
      } else if (!newValue || isTextContentEmpty(newValue)) {
        editorInstance.setContents([]);
      }
    },
  );

  // Watch model and update modelValue if has changes
  watch(model, (newValue, oldValue) => {
    if (!editorInstance) return;
    if (newValue && newValue !== oldValue) {
      if (!isTextContentEmpty(newValue)) {
        emit('update:model-value', newValue);
      } else {
        emit('update:model-value', '');
      }
    } else if (!newValue || isTextContentEmpty(newValue)) {
      editorInstance.setContents([]);
      emit('update:model-value', '');
    }
  });

  onBeforeUnmount(() => {
    editorInstance = null;
  });
</script>

<template>
  <Field v-bind="props" class="stash-text-editor" :class="[classes.root]" data-test="stash-text-editor">
    <div
      v-if="!props.isReadOnly"
      :id="`${editorId}-format-bar`"
      class="stash-text-editor__format-bar"
      :class="[classes['format-bar-root'], { '!border-x-red-500 !border-t-red-500': !!props.errorText }]"
      data-test="stash-text-editor|format-bar"
      role="format-bar"
    >
      <template v-for="(control, idx) in computedControls" :key="idx">
        <div
          v-if="Array.isArray(control)"
          class="stash-text-editor__format-bar__group"
          :class="classes['format-bar-group']"
          data-test="stash-text-editor|format-bar-group"
        >
          <Button
            v-for="(format, formatIdx) in control"
            :key="format.name + formatIdx"
            class="stash-text-editor__format-bar__control"
            :aria-label="format.value ? `${format.name}: ${format.value}` : `${format.name}`"
            :data-test="`stash-text-editor|format-bar-${format.name}-grouped-control`"
            :class="[classes['format-bar-control'], `ql-${format.name}`]"
            :disabled="props.isDisabled"
            :value="format.value"
            secondary
            icon
          >
            <Icon v-if="format.icon" :name="format.icon" size="dense" />
          </Button>
        </div>

        <Button
          v-else
          class="stash-text-editor__format-bar__control"
          :data-test="`stash-text-editor|format-bar-${control.name}-control`"
          :aria-label="control.value ? `${control.name}: ${control.value}` : `${control.name}`"
          :class="[classes['format-bar-control'], `ql-${control.name}`]"
          :disabled="props.isDisabled"
          :value="control.value"
          secondary
          icon
        >
          <Icon v-if="control.icon" :name="control.icon" size="dense" />
        </Button>
      </template>
    </div>
    <div
      :id="editorId"
      ref="editorRef"
      data-test="stash-text-editor|editor"
      class="stash-text-editor__content-container"
      :class="[classes['content-container']]"
    ></div>
  </Field>
</template>

<style module>
  @reference "../../../styles/main.css";

  /* Quill's stylesheet loads into the low-priority `vendor` layer (see TextEditor.init.ts), so these overrides win. */
  .root {
    :global(.ql-editor) {
      font-family: var(--font-sans);
      min-height: v-bind(minHeightCss);
      max-height: v-bind(maxHeightCss);
      overflow-y: auto;
      padding: --spacing(2) --spacing(3);

      /* Quill's placeholder content */
      &::before {
        color: var(--color-ice-500);
        left: --spacing(3);
        right: --spacing(3);
      }

      hr {
        border: 1px solid var(--color-ice-200);
        margin: 8px 0;
      }
    }

    :global(.ql-container) {
      height: 100%;
      border: 1px solid var(--color-ice-500);
      border-top: 0 !important;
      border-radius: 0 0 var(--radius-sm) var(--radius-sm);
      background: var(--color-white);
      color: var(--color-ice-900);
    }

    &:global(.stash-field--has-error .ql-container) {
      border: 1px solid var(--color-red-500) !important;
    }

    &:global(.stash-field--is-disabled .ql-container) {
      background: var(--color-ice-100);
      color: 1px solid var(--color-ice-500);
    }

    &:global(.stash-field--is-read-only .ql-container) {
      border: 0;
      background: unset;
    }
  }

  .format-bar-root:global(.stash-text-editor__format-bar) {
    display: flex;
    flex-wrap: wrap;
    gap: --spacing(1.5);
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;
    border: 1px solid var(--color-ice-500);
    background-color: var(--color-ice-100);
    padding: --spacing(1.5);
    font-family: var(--font-sans);

    .format-bar-group {
      display: flex;
      align-items: center;
      margin-right: 0;

      :global(.stash-text-editor__format-bar__control) {
        border-radius: 0;

        &:first-child {
          border-top-left-radius: var(--radius-sm);
          border-bottom-left-radius: var(--radius-sm);
        }

        &:last-child {
          border-top-right-radius: var(--radius-sm);
          border-bottom-right-radius: var(--radius-sm);
        }

        &:not(:last-child) {
          border-right: 0;
        }
      }
    }

    .format-bar-control {
      display: flex;
      align-items: center;
      justify-content: center;
      width: --spacing(9);
      height: --spacing(9);
      min-width: auto !important;
      padding: --spacing(1.5);
      background: var(--color-white);
      border: 1px solid var(--color-ice-500);
      float: none !important; /* removing Quill's default style */
      color: var(--color-ice-700) !important;

      svg {
        float: none !important; /* removing Quill's default style */
      }

      &:hover,
      &:focus,
      &[disabled] {
        background: var(--color-ice-100);
      }

      &:global(.ql-active) {
        background: var(--color-blue-100);
      }
    }
  }
</style>
